import { CommonState, ListParams, Pagination } from './common';
import { PaymentTypes } from 'config';
import { Country } from './country';

export const enum ClientSex {
  Female = 0,
  Male = 1,
}

export interface IOrderPayment {
  created_at: string | null;
  expired_at: string | null;
  paid_at: string | null;
  id: number;
  link: string;
  price: number;
  product_id: number | null;
  status: number;
  product: { id: number; title: string } | null;
  payment_type: string | null;
}

export interface Card {
  id: number;
  active: boolean;
  client_id: number;
  created_at: string;
  data: string;
  deleted_at: string | null;
  recurrent_root: string;
  revoked: false;
  updated_at: string;
}

export enum TypesCardValues {
  mir = 'Mir',
  maestro = 'Maestro',
  visa = 'Visa',
  masterCard = 'MasterCard',
  robokassa = 'robokassa',
}

export interface ICardInform {
  card_type: TypesCardValues;
  expiry_month: string;
  expiry_year: string;
  first6: string;
  last4: string;
  id: number;
  active: boolean;
}

export enum WebPlayersTypes {
  KINESCOPE_WEB = 'kinescope_web',
  BOOMSTREAM_WEB = 'boomstream_web',
}

export enum NativePlayersTypes {
  KINESCOPE_NATIVE = 'kinescope_native',
  BOOMSTREAM_NATIVE = 'boomstream_native',
}

export type PlayerType = WebPlayersTypes | NativePlayersTypes;

export const ALL_PLAYER_TYPES: PlayerType[] = [
  WebPlayersTypes.KINESCOPE_WEB,
  WebPlayersTypes.BOOMSTREAM_WEB,
  NativePlayersTypes.KINESCOPE_NATIVE,
  NativePlayersTypes.BOOMSTREAM_NATIVE,
];

export const WEBVIEW_PLAYER_TYPES: PlayerType[] = [
  WebPlayersTypes.KINESCOPE_WEB,
  WebPlayersTypes.BOOMSTREAM_WEB,
  NativePlayersTypes.KINESCOPE_NATIVE,
];

export const WEB_PLAYER_TYPES: PlayerType[] = [
  WebPlayersTypes.KINESCOPE_WEB,
  WebPlayersTypes.BOOMSTREAM_WEB,
];

export const NATIVE_PLAYER_TYPES: PlayerType[] = [
  NativePlayersTypes.KINESCOPE_NATIVE,
  NativePlayersTypes.BOOMSTREAM_NATIVE,
];

export enum PlayerPlatform {
  WEB = 'web',
  WEBVIEW = 'webview',
  IOS = 'ios',
  ANDROID = 'android',
  HUAWEI = 'huawei',
}

export const PLATFORM_PLAYER_TYPES: Record<PlayerPlatform, PlayerType[]> = {
  [PlayerPlatform.WEB]: WEB_PLAYER_TYPES,
  [PlayerPlatform.WEBVIEW]: WEBVIEW_PLAYER_TYPES,
  [PlayerPlatform.IOS]: NATIVE_PLAYER_TYPES,
  [PlayerPlatform.ANDROID]: NATIVE_PLAYER_TYPES,
  [PlayerPlatform.HUAWEI]: NATIVE_PLAYER_TYPES,
};

/** Настройки плеера клиента по платформам */
export type IClientPlayers = {
  [PlayerPlatform.WEB]: WebPlayersTypes | undefined;
  [PlayerPlatform.WEBVIEW]: PlayerType | undefined;
  [PlayerPlatform.IOS]: NativePlayersTypes | undefined;
  [PlayerPlatform.ANDROID]: NativePlayersTypes | undefined;
  [PlayerPlatform.HUAWEI]: NativePlayersTypes | undefined;
};

/** Ответ админского GET /admin/clients/{id}/player */
export interface IAdminClientPlayersResponse extends IClientPlayers {
  global?: Partial<IClientPlayers>;
}

/** Глобальный плеер — настройки администратора */
export type IGPlayerSettings = Partial<IClientPlayers>;

/** @deprecated Используйте IClientPlayers */
export interface IUserPlayer {
  mobile?: WebPlayersTypes | NativePlayersTypes;
  desktop?: WebPlayersTypes;
}

interface VerPlatform {
  android: string;
  ios: string;
  huawei: string;
  web: string;
  desktop: string;
  'native-ios': string;
  'native-android': string;
  'native-huawei': string;
}

export type Client = {
  id: number;
  name: string;
  dob: string;
  phone: string;
  email: string;
  avatar: string | null;
  created_at: string;
  active: boolean | null;
  sex: ClientSex;
  new_password: string;
  newsletter: boolean;
  country_id?: number;
  country: Country;
  autopay: boolean;
  orders: IOrderPayment[];
  trusted: boolean;
  mute: boolean;
  referral_mode: 0 | 1 | undefined;
  cards: Card[];
  auto_renewal: boolean;
  reg_code?: string | null;
  ver_platform: VerPlatform | null;
  frozen_at: string | null;
  unfrozen_at: string | null;
  subscription_tariff: string | null;
  subscription_expired_at: string | null;
  /** @deprecated Используйте IClientPlayers из админского API /admin/clients/{id}/player */
  player: IUserPlayer | null;
};

export type ClientList = {
  data?: Client[];
  meta?: Pagination;
};

export const enum ClientPlatformType {
  Autoweboffice = 1,
  Gurucan = 2,
}

export type ClientPlatform = {
  platform: ClientPlatformType;
  platform_id: string;
};

export enum ClientStatus {
  all = '',
  active = 'active',
  unactive = 'unactive',
  disabled = 'disabled',
  frozen = 'frozen',
}

export const enum ClientPlatformString {
  'all' = '',
  'Autoweboffice' = 1,
  'Gurucan' = 2,
}

export enum ClientPayment {
  all = '',
  live = 'live',
  was = 'was',
  expired = 'expired',
  nil = 'nil',
  expiredNil = 'expired-nil',
  frozen = 'frozen',
}

export enum TrialFilterTypes {
  LIVE = 'live',
  WAS = 'was',
  NIL = 'nil',
}

export enum DateTypes {
  CREATED_AT_START = 'created_at[start]',
  CREATED_AT_END = 'created_at[end]',
  CANCELED_AT_START = 'canceled_at[start]',
  CANCELED_AT_END = 'canceled_at[end]',
  EXPIRED_AT_START = 'expired_at[start]',
  EXPIRED_AT_END = 'expired_at[end]',
  SUBSCRIPTION_EXPIRED_AT_START = 'subscription_expired_at[start]',
  SUBSCRIPTION_EXPIRED_AT_END = 'subscription_expired_at[end]',
  PAID_AT_START = 'paid_at[start]',
  PAID_AT_END = 'paid_at[end]',
  FROM = 'from',
  TO = 'to',
}

export enum PaymentsCountTypes {
  PAYMENTS_COUNT_FROM = 'payments_count[from]',
  PAYMENTS_COUNT_TO = 'payments_count[to]',
}

export enum DaysLeftTypes {
  DAYS_LEFT_FROM = 'days_left[from]',
  DAYS_LEFT_TO = 'days_left[to]',
}

export type CreatedDates = {
  [DateTypes.CREATED_AT_START]?: string;
  [DateTypes.CREATED_AT_END]?: string;
};

export type ExpiredDates = {
  [DateTypes.EXPIRED_AT_START]?: string;
  [DateTypes.EXPIRED_AT_END]?: string;
};

export type PaidDates = {
  [DateTypes.PAID_AT_START]?: string;
  [DateTypes.PAID_AT_END]?: string;
};

export type СanceledDates = {
  [DateTypes.CANCELED_AT_START]?: string;
  [DateTypes.CANCELED_AT_END]?: string;
};

export type SubscriptionExpiredDates = {
  [DateTypes.SUBSCRIPTION_EXPIRED_AT_START]?: string;
  [DateTypes.SUBSCRIPTION_EXPIRED_AT_END]?: string;
};

export type ChartDates = {
  [DateTypes.FROM]?: string;
  [DateTypes.TO]?: string;
};

export type ProductIDs = {
  product_ids?: string;
};

export type PaymentsCount = {
  [PaymentsCountTypes.PAYMENTS_COUNT_FROM]?: number;
  [PaymentsCountTypes.PAYMENTS_COUNT_TO]?: number;
};

export type DaysLeft = {
  [DaysLeftTypes.DAYS_LEFT_FROM]?: number;
  [DaysLeftTypes.DAYS_LEFT_TO]?: number;
};

export enum HasEmailValues {
  YES = 'yes',
  NO = 'no',
  ALL = 'all',
}

export type ClientListParams = ListParams &
  CreatedDates &
  PaidDates &
  СanceledDates &
  SubscriptionExpiredDates &
  PaymentsCount &
  DaysLeft &
  ProductIDs & {
    scan?: string;
    platformType?: ClientPlatformType;
    active?: boolean | 'null'; // необходимо для корректной отправки значение null в query запросе
    paid?: ClientPayment;
    newsletter?: boolean;
    autopay?: boolean;
    had_payment?: boolean;
    trial?: TrialFilterTypes;
    has_email?: HasEmailValues;
  };

export interface ClientFreezeOrder {
  id: number;
  product: string;
  payment_type: PaymentTypes;
  created_at: string;
  paid_at: string;
  frozen_days: number;
  expired_at_old: string | null;
}

export interface CommentFrozen {
  text: string;
  user_id: string;
  user_name: string;
}

export interface ClientFreeze {
  frozen_at: string;
  unfrozen_at: string;
  comment_frozen: CommentFrozen | null;
  comment_unfrozen: CommentFrozen | null;
  freezing_period: number | null;
  freezing_default: number | null;
  expired_at_new: string | null;
  orders: ClientFreezeOrder[];
}

export type clientFreezeData = Record<number, ClientFreeze>;

export type ClientState = CommonState & {
  client?: Client;
  clientList: ClientList;
  clientListParams: ClientListParams;
  filtersListParams: ClientListParams;
  tableFiltersListParams: ClientListParams;
  filtersErrors: PaidDates & CreatedDates & ProductIDs;
  clientFreezeData: clientFreezeData;
};

export interface ICreateEditClientParams {
  name?: string;
  phone?: string;
  email: string | null;
  avatar: string | null;
  active: boolean | null;
  dob?: string;
  referral_mode?: 0 | 1;
  mute: boolean;
  newsletter: boolean;
  /** @deprecated Используйте IClientPlayers через PUT /admin/clients/{id}/player */
  player: IUserPlayer | null;
}

export interface ShortEditParams {
  trusted: boolean;
  newsletter: boolean;
}

export interface IFreezeClient {
  unfrozen_at: string | null;
  comment: string;
}

export interface IAuthMethod {
  available: boolean;
  priority: number;
  platforms: string[];
}

export type IAuthMethodsMap = Record<string, IAuthMethod>;

export interface IAuthMethodsResponse {
  data: IAuthMethodsMap;
}

export interface IAuthMethodsRequest {
  methods: IAuthMethodsMap;
}

export const AUTH_PLATFORM_OPTIONS = [
  { value: 'web', label: 'Web' },
  { value: 'webview', label: 'WebView' },
  { value: 'ios', label: 'iOS (натив)' },
  { value: 'android', label: 'Android (натив)' },
  { value: 'huawei', label: 'Huawei (натив)' },
] as const;
